import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useToast } from '@/components/common/toast';
import { Select2Field } from '@/components/forms/Select2Field';
import type { SelectOption } from '@/components/forms/SelectField';
import { TextField } from '@/components/forms/TextField';
import { ApiError } from '@/api/client/abpError';
import { TabActions, TabShell } from './TabShell';
import {
  useCountries,
  useCurrenciesWithSymbols,
  useTenantInfo,
  useTenantSetting,
  useUpdateClientInfo,
} from '../api';
import { useTenantEnumOptions } from '../enumOptions';

interface ClientInfoForm {
  typeOfEntity: string;
  maxCoverageOfFunds: string;
  leiCode: string;
  prudentialClassification: string;
  permanentCapitalRequirement: string;
  countryId: string;
  timeZone: string;
  reportingCurrencyId: string;
  ccyCurrencyId: string;
  balanceHaircutPercentage: string;
  productType: string;
  basisOfProvisionOfServices: string;
  instrumentProfitabilityIncludeLp: boolean;
}

export function ClientInfoTab({ tenantId, onCancel }: { tenantId: string; onCancel: () => void }) {
  const toast = useToast();
  const infoQuery = useTenantInfo(tenantId);
  const settingQuery = useTenantSetting(tenantId);
  const countriesQuery = useCountries(tenantId);
  const currenciesQuery = useCurrenciesWithSymbols(tenantId);
  const updateMut = useUpdateClientInfo(tenantId);

  const { register, handleSubmit, reset, control } = useForm<ClientInfoForm>();
  const {
    basisOfProvisionOptions,
    permanentCapitalRequirementOptions,
    productTypeOptions,
    prudentialClassificationOptions,
    typeOfEntityOptions,
  } = useTenantEnumOptions();

  useEffect(() => {
    if (infoQuery.data && settingQuery.data) {
      const i = infoQuery.data;
      const s = settingQuery.data;
      reset({
        typeOfEntity: String(i.typeOfEntity),
        maxCoverageOfFunds: String(i.maxCoverageOfFunds ?? ''),
        leiCode: i.leiCode ?? '',
        prudentialClassification: String(i.prudentialClassification),
        permanentCapitalRequirement: String(i.permanentCapitalRequirement),
        countryId: i.countryId ? String(i.countryId) : '',
        timeZone: i.timeZone ?? '',
        reportingCurrencyId: s.reportingCurrencyId ? String(s.reportingCurrencyId) : '',
        ccyCurrencyId: s.ccyCurrencyId ? String(s.ccyCurrencyId) : '',
        balanceHaircutPercentage: String(i.balanceHaircutPercentage ?? ''),
        productType: String(i.productType),
        basisOfProvisionOfServices: String(i.basisOfProvisionOfServices),
        instrumentProfitabilityIncludeLp: s.instrumentProfitabilityIncludeLp ?? false,
      });
    }
  }, [infoQuery.data, settingQuery.data, reset]);

  const countryOptions: SelectOption[] = useMemo(
    () => (countriesQuery.data ?? []).map((c) => ({ value: c.value, label: c.text })),
    [countriesQuery.data],
  );
  const currencyOptions: SelectOption[] = useMemo(
    () => (currenciesQuery.data ?? []).map((c) => ({ value: c.value, label: c.text })),
    [currenciesQuery.data],
  );

  const isLoading =
    infoQuery.isLoading || settingQuery.isLoading || countriesQuery.isLoading || currenciesQuery.isLoading;
  const isError = infoQuery.isError || settingQuery.isError;

  function onSubmit(values: ClientInfoForm) {
    updateMut.mutate(
      {
        tenantInformationCreateDto: {
          tenantId,
          typeOfEntity: Number(values.typeOfEntity),
          maxCoverageOfFunds: Number(values.maxCoverageOfFunds),
          leiCode: values.leiCode.trim() || null,
          prudentialClassification: Number(values.prudentialClassification),
          permanentCapitalRequirement: Number(values.permanentCapitalRequirement),
          countryId: Number(values.countryId),
          timeZone: values.timeZone || null,
          balanceHaircutPercentage: Number(values.balanceHaircutPercentage),
          productType: Number(values.productType),
          basisOfProvisionOfServices: Number(values.basisOfProvisionOfServices),
        },
        tenantSettingCreateDto: {
          tenantId,
          reportingCurrencyId: Number(values.reportingCurrencyId),
          ccyCurrencyId: Number(values.ccyCurrencyId),
          instrumentProfitabilityIncludeLp: values.instrumentProfitabilityIncludeLp,
        },
      },
      {
        onSuccess: () => toast.success('Client info saved.'),
        onError: (err) =>
          toast.error(err instanceof ApiError ? err.message : 'Failed to save client info.'),
      },
    );
  }

  return (
    <TabShell isLoading={isLoading} isError={isError} onRetry={() => infoQuery.refetch()}>
      <TabActions busy={updateMut.isPending} onCancel={onCancel} formId="clientInfoForm" />
      <form id="clientInfoForm" onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="row mt-3">
          <div className="col-md-12 col-lg-4">
            <Select2Field
              control={control}
              name="typeOfEntity"
              label="Type Of Entity"
              placeholder="Please select..."
              options={typeOfEntityOptions}
            />
          </div>
          <div className="col-lg-4">
            <TextField label="Max Coverage Of Funds" type="number" {...register('maxCoverageOfFunds')} />
          </div>
          <div className="col-lg-4">
            <TextField label="LEI Code" {...register('leiCode')} />
          </div>
          <div className="col-lg-4">
            <Select2Field
              control={control}
              name="prudentialClassification"
              label="Prudential Classification"
              options={prudentialClassificationOptions}
            />
          </div>
          <div className="col-lg-4">
            <Select2Field
              control={control}
              name="permanentCapitalRequirement"
              label="Permanent Capital Requirement"
              options={permanentCapitalRequirementOptions}
            />
          </div>
          <div className="col-lg-4">
            <Select2Field
              control={control}
              name="countryId"
              label="Country Of Incorporation"
              placeholder="Please select..."
              options={countryOptions}
            />
          </div>
          <div className="col-lg-4">
            {/* Legacy renders a server-built time-zone select (TimeZoneHelper);
                no JSON endpoint exists, so a free-text IANA id is used here. */}
            <TextField label="Time Zone" placeholder="e.g. Europe/London" {...register('timeZone')} />
          </div>
          <div className="col-lg-4">
            <Select2Field
              control={control}
              name="reportingCurrencyId"
              label="Reporting Currency"
              placeholder="Please select..."
              options={currencyOptions}
            />
          </div>
          <div className="col-lg-4">
            <Select2Field
              control={control}
              name="ccyCurrencyId"
              label="CCY Currency"
              placeholder="Please select..."
              options={currencyOptions}
            />
          </div>
          <div className="col-lg-4">
            <TextField
              label="Balance Haircut %"
              type="number"
              step="any"
              {...register('balanceHaircutPercentage')}
            />
          </div>
          <div className="col-lg-4">
            <Select2Field
              control={control}
              name="productType"
              label="Product Type"
              options={productTypeOptions}
            />
          </div>
          <div className="col-lg-4">
            <Select2Field
              control={control}
              name="basisOfProvisionOfServices"
              label="Basis Of Provision Of Services"
              options={basisOfProvisionOptions}
            />
          </div>
          <div className="col-lg-4">
            <div className="form-check form-switch mt-4">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="instrumentProfitabilityIncludeLp"
                {...register('instrumentProfitabilityIncludeLp')}
              />
              <label className="form-check-label" htmlFor="instrumentProfitabilityIncludeLp">
                Instrument Profitability Include LP
              </label>
            </div>
          </div>
        </div>
      </form>
    </TabShell>
  );
}
